import React, { useId } from "react";
import LinkedText from "../../../shared/components/ui/LinkedText";
import { Box } from "../../../shared/components/ui/chakra";
import { BusinessEntityChip } from "./BusinessEntityChip";
import BusinessEntityContentsCard from "./BusinessEntityContentsCard";
import BusinessEntityDottedLine from "./BusinessEntityDottedLine";
import ExpandableText from "./ExpandableText";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import ArrowForwardIcon from "../../../shared/components/icons/ArrowForwardIcon";
import { Helmet } from "react-helmet";

import { HStack, Stack } from "@chakra-ui/react";

const BusinessEntityDesignWorkCard = ({
  designWork,
}: {
  designWork: {
    project_name: string;
    project_detail: string;
    building_type: { name: string; id: number };
    structure_types: { name: string; id: number }[];
    prefecture: { name: string; id: number } | null;
    above_ground_floors: number | null;
    underground_floors: number | null;
    completion_year: number | null;
    total_floor_area: number | null;
    images: { url: string }[];
  };
}) => {
  const id = useId();
  const hasImages = designWork.images.length > 0;

  return (
    <Box as="article">
      {hasImages && (
        <>
          <Helmet>
            <style>
              {`
          .swiper-pagination-bullet {
            width: 0.5rem;
            height: 0.5rem;
            background-color: #9D9D9D;
          }

          .swiper-pagination-bullet-active {
            width: 1rem;
            height: 1rem;
            background-color: #9D9D9D;
          }
       `}
            </style>
          </Helmet>
          <HStack position="relative">
            <Box
              as="button"
              id={`prev-${id}`}
              p={2}
              bgColor="rgba(255, 255, 255, 0.7)"
              rounded="full"
              position="absolute"
              zIndex={10}
              left={4}
            >
              <ArrowForwardIcon boxSize={7} transform="rotate(180deg)" />
            </Box>
            <Box w="full" roundedTop={8} overflow="hidden">
              <Swiper
                spaceBetween={50}
                slidesPerView={1}
                modules={[Navigation, Pagination]}
                navigation={{
                  prevEl: `#${CSS.escape(`prev-${id}`)}`,
                  nextEl: `#${CSS.escape(`next-${id}`)}`,
                }}
                pagination={{
                  el: `#${CSS.escape(`pagination-${id}`)}`,
                }}
              >
                {designWork.images.map((image, index) => (
                  <SwiperSlide key={index}>
                    <Box as="img" src={image.url} alt="" w="full" />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>
            <Box
              as="button"
              id={`next-${id}`}
              p={2}
              bgColor="rgba(255, 255, 255, 0.7)"
              rounded="full"
              zIndex={10}
              right={4}
              position="absolute"
            >
              <ArrowForwardIcon boxSize={7} />
            </Box>
          </HStack>
          {designWork.images.length > 1 && (
            <Box pt={4} bgColor="white">
              <HStack id={`pagination-${id}`} gap={0} justifyContent="center" />
            </Box>
          )}
        </>
      )}
      <BusinessEntityContentsCard
        pt={{ base: 5, md: 7 }}
        roundedTop={hasImages ? 0 : 8}
      >
        <Box as="h3" fontSize={{ base: "lg", md: "2xl" }} fontWeight="bold">
          {designWork.project_name}
        </Box>
        <HStack
          as="section"
          mt={1}
          rowGap={1}
          columnGap={0}
          fontSize={{ base: "xs", md: "sm" }}
          flexWrap="wrap"
        >
          {[
            designWork.prefecture ? designWork.prefecture.name : null,
            designWork.above_ground_floors
              ? `地上${designWork.above_ground_floors}階建て`
              : null,
            designWork.underground_floors
              ? `地下${designWork.underground_floors}階建て`
              : null,
            designWork.total_floor_area
              ? `延床面積：${designWork.total_floor_area.toLocaleString()}㎡`
              : null,
            designWork.completion_year
              ? `竣工年：${designWork.completion_year}年`
              : null,
          ]
            .filter(Boolean)
            .map((text, index, original) => (
              <HStack key={text} gap={0}>
                <Box display="inline-block" mt="1px">
                  {text}
                </Box>
                {index < original.length - 1 && (
                  <Box
                    mx={3}
                    h={4}
                    w="1px"
                    bgColor="brand.300"
                    display="inline-block"
                  />
                )}
              </HStack>
            ))}
        </HStack>
        <Stack as="section" mt={4} gap={{ base: 2, md: 1 }}>
          {[
            {
              sectionTitle: "建物種別",
              contents: [designWork.building_type.name],
            },
            {
              sectionTitle: "構造種別",
              contents: designWork.structure_types.map(
                (structureType) => structureType.name,
              ),
            },
          ].map(({ sectionTitle, contents }) => (
            <HStack as="section" key={sectionTitle} gap={1}>
              <Box as="h4" fontSize="xs">
                {sectionTitle}：
              </Box>
              <HStack gap={2} flexWrap="wrap">
                {contents.map((text) => (
                  <BusinessEntityChip key={text}>{text}</BusinessEntityChip>
                ))}
              </HStack>
            </HStack>
          ))}
        </Stack>
        <BusinessEntityDottedLine mt={{ base: 5, md: 6 }} />
        <Box whiteSpace="pre-wrap" fontSize="sm" mt={{ base: 5, md: 6 }}>
          <ExpandableText
            foldingMaxHeight={400}
            showExpandButtonLabel="紹介文の続きを読む"
          >
            <LinkedText>{designWork.project_detail}</LinkedText>
          </ExpandableText>
        </Box>
      </BusinessEntityContentsCard>
    </Box>
  );
};

export default BusinessEntityDesignWorkCard;
